"use strict";

import * as ResultTypes from '@resultful/types';
export { ResultTypes };
/**
 * Signifies that something 'worked'. Also known as a "happy path" result.
 */

/**
 * Check if a result is an {@link ExceptionResult}.
 * @param result being checked
 * @return Returning true signifies that some unexpected (i.e. API creator did not expect/account for the errror) erroneuous behaviour has occurred.
 */
export const isException = result => result.type === ResultTypes.EXCEPTION;
/**
 * Check if a result is an {@link ErrorResult}.
 * @param result being checked
 * @return Returning true signifies expected (i.e. API creator is aware of the error) erroneuous behaviour has occurred.
 */

export const isError = result => result.type === ResultTypes.ERROR;

/**
 * Check if a result is a {@link PayloadResult}.
 * @param result being checked
 * @return Returning true signifies successful/"happy path" behaviour has occurred.
 */
export const isPayload = result => result.type === ResultTypes.SUCCESS;
/**
 * Alias for {@link isPayload}
 */

export const isNormal = isPayload;
/**
 * Alias for {@link isPayload}
 */

export const isSuccess = isPayload;
/**
 * Checks if a result is a {@link FailureResult}.
 * @param result being checked
 * @returns Returning true signifies that erroneous behaviour has occurred (i.e. An {@link exception} or an {@link error}).
 */

export const isFailure = result => {
  switch (result.type) {
    case ResultTypes.ERROR:
    case ResultTypes.EXCEPTION:
      {
        return true;
      }

    default:
      return false;
  }
};

/**
 * Use this to create a {@link PayloadResult} which signifies that something successful
 * has happened and your API has run as expected and has been consumed correctly (AKA the "happy path").
 */
export const payload = payload => ({
  type: ResultTypes.SUCCESS,
  payload,
  error: undefined,
  exception: undefined
});
/**
 * Alias for {@link payload}
 */

export const success = payload;
/**
 * Alias for {@link payload}
 */

export const normal = payload;

/**
 * Use this to create an {@link ErrorResult} which signifies that either your API has behaved erroneuously in some way but
 * you're aware that the error can occur.
 */
export const error = error => ({
  type: ResultTypes.ERROR,
  payload: undefined,
  error: error,
  exception: undefined
});

/**
 * Use this to create an {@link ExceptionResult} which signifies that some unexpected,
 * thought-to-be-impossible/didn't-think-about-it type erroneous behaviour has ocurred.
 */
export const exception = exception => {
  return {
    type: ResultTypes.EXCEPTION,
    payload: undefined,
    error: undefined,
    exception
  };
};

/**
 * This is exactly the same as {@link handle} except there is no try { ... } catch { ... } wrapper around the handlers.
 * This may improve performance but removes the guarentee that nothing will ever be thrown by the handle function.
 */
export const catchlessHandle = (result, handlers = {}) => {
  switch (result.type) {
    case ResultTypes.SUCCESS:
      {
        const {
          payload
        } = result;

        if (handlers.payload !== undefined) {
          return handlers.payload(payload, result);
        }

        break;
      }

    case ResultTypes.ERROR:
      {
        const {
          error
        } = result;

        if (handlers.error !== undefined) {
          return handlers.error(error, result);
        }

        break;
      }

    case ResultTypes.EXCEPTION:
      {
        const {
          exception
        } = result;

        if (handlers.exception !== undefined) {
          return handlers.exception(exception, result);
        }

        break;
      }
  }

  return result;
};

/**
 * A helper function which executes a relevant handler based on the type of result that is passed into the function.
 * @param result The result that will be handled by one of the applicable handlers
 * @param handlers Callbacks that are used
 * @return Returns whatever is returned by the applicable handler. If no handler for the result exists, the orignal result is returned. Note that if something is thrown by one of the handlers, it is caught by this function and the value of what is caught is returned as via {@link exception}.
 */
export const handle = (result, handlers = {}) => {
  try {
    return catchlessHandle(result, handlers);
  } catch (err) {
    return exception(err);
  }
};